import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import { Flame, MapPin, Shield, Target, Users, Zap } from "lucide-react";
import { motion } from "motion/react";
import LeadForm from "../components/LeadForm";

const benefits = [
  {
    icon: Zap,
    title: "Improve Balance & Explosiveness",
    desc: "Develop the athleticism and body control needed to explode into takedowns and maintain dominant positions.",
  },
  {
    icon: Target,
    title: "Learn Takedowns & Control",
    desc: "Master single legs, double legs, and upper body entries that give you the ability to take the fight where you want it.",
  },
  {
    icon: Flame,
    title: "Enhance Jiu Jitsu & MMA Performance",
    desc: "Wrestling is the bridge between stand-up and the ground — elevate your entire grappling game with elite takedown skills.",
  },
];

const trainingSteps = [
  {
    number: "01",
    title: "Drilling & Technique",
    desc: "Every class starts with focused drilling on entries, setups, and finishes. Repetition builds the muscle memory to execute under pressure.",
  },
  {
    number: "02",
    title: "Live Training",
    desc: "Apply your skills in live rounds with training partners of all levels. Controlled intensity allows you to sharpen your wrestling in real time.",
  },
  {
    number: "03",
    title: "Integration with Other Programs",
    desc: "Wrestling principles are woven into our Jiu Jitsu and MMA classes so your skills carry over — creating a complete, seamless grappling system.",
  },
];

const audiences = [
  {
    icon: Users,
    title: "Jiu Jitsu Students",
    desc: "Add powerful takedowns to your grappling game and stop being stuck in guard pulls. Wrestling makes your Jiu Jitsu complete.",
  },
  {
    icon: Flame,
    title: "MMA Athletes",
    desc: "Control where the fight happens. Wrestling gives you the ability to take opponents down, stay on top, and dictate the pace.",
  },
  {
    icon: Shield,
    title: "Beginners",
    desc: "No experience needed. Our coaches break wrestling down into digestible fundamentals so anyone can start building real skills from day one.",
  },
];

const serviceAreas = ["Eugene", "Thurston", "Glenwood", "Fairmont"];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const fadeLeft = (delay = 0) => ({
  initial: { opacity: 0, x: -30 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

const fadeRight = (delay = 0) => ({
  initial: { opacity: 0, x: 30 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay },
});

export default function WrestlingPage() {
  const router = useRouter();

  return (
    <Layout>
      <title>
        Wrestling Classes in Springfield, OR | Superhero Jiu Jitsu Academy
      </title>
      <meta
        name="description"
        content="Learn wrestling take downs and holds at Superhero Jiu Jitsu Academy in Springfield, OR. We combine traditional and modern wrestling techniques for adults, teens, and kids of all skill levels."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative min-h-[92vh] flex items-center"
        data-ocid="wrestling-hero"
      >
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/assets/images/wrestling hero image.jpg')",
          }}
          aria-hidden="true"
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, oklch(0.10 0.06 255 / 0.97) 0%, oklch(0.14 0.07 270 / 0.88) 50%, oklch(0.12 0.05 255 / 0.70) 100%)",
          }}
          aria-hidden="true"
        />
        {/* Gold accent overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, oklch(0.68 0.16 82) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        {/* Gold top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <motion.div {...fadeLeft(0.1)}>
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
                Wrestling · Springfield, OR
              </span>
              <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5">
                Wrestling Training in{" "}
                <span className="text-gold">Springfield, OR</span>
              </h1>
              <p className="text-hero-foreground font-body text-lg leading-relaxed max-w-lg mb-8">
                Build takedowns, control, and explosive movement for real
                performance.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "Takedowns",
                  "Mat Control",
                  "Explosive Movement",
                  "Beginners Welcome",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/20 border border-gold/40 text-sm font-body font-medium text-hero-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right — lead capture form */}
            <motion.div {...fadeRight(0.2)}>
              <div
                className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
                data-ocid="wrestling-hero-form"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
                <h2 className="font-display font-bold text-xl text-hero-foreground mb-1">
                  Book Your Free Class
                </h2>
                <p className="text-hero-foreground font-body text-sm mb-6 opacity-80">
                  First class is on us. No experience required. Must book in
                  advance.
                </p>

                <LeadForm />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── INTRO SECTION ── */}
      <section className="bg-background py-16 px-4" data-ocid="wr-intro">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeLeft(0.1)}>
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                Control · Takedowns · Mat Pressure
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-6 text-balance">
                The Foundation of Every Great Grappler
              </h2>
              <div className="space-y-4 text-muted-foreground font-body text-base leading-relaxed">
                <p>
                  Wrestling is the backbone of grappling. Whoever controls where
                  the fight happens controls the outcome — and that starts with
                  wrestling. Takedowns, body control, and relentless pressure
                  are what separate good grapplers from great ones.
                </p>
                <p>
                  At Superhero Jiu Jitsu Academy, wrestling classes give you the
                  takedown skills and mat control that round out your grappling.
                  You will learn entries, finishes, and rides that blend
                  seamlessly with your Jiu-Jitsu. Build explosive movement,
                  balance, and pressure with sessions that focus on practical
                  application.
                </p>
              </div>
            </motion.div>

            {/* Visual card */}
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <img
                src="/assets/images/Superhero Jiu-Jitsu Acadmey_Wrestling.jpg"
                alt="Instructor coaching a student during Jiu Jitsu class"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="wr-benefits"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div {...fadeUp(0.1)}>
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Real Results
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-12 text-balance">
              Why Train Wrestling?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} {...fadeUp(0.1 + i * 0.1)}>
                <div className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center gap-4 shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5 h-full">
                  <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                    <Icon className="w-5 h-5 text-foreground" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-base text-foreground mb-2">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground leading-snug">
                      {desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRAINING APPROACH SECTION ── */}
      <section className="bg-hero py-16 px-4" data-ocid="wr-training">
        <div className="container mx-auto max-w-4xl">
          <motion.div {...fadeUp(0.1)} className="text-center mb-10">
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              How We Train
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-hero-foreground text-balance mb-4">
              Train with Purpose
            </h2>
            <p className="text-hero-foreground font-body text-base leading-relaxed max-w-2xl mx-auto opacity-80">
              Our wrestling curriculum is built around progressive drilling,
              live training, and seamless integration with our Jiu-Jitsu and MMA
              programs. Every session has a clear goal — and you leave better
              than when you came in.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
            {trainingSteps.map(({ number, title, desc }, i) => (
              <motion.div key={number} {...fadeUp(0.15 + i * 0.1)}>
                <div className="bg-primary/60 backdrop-blur-sm border border-gold/20 rounded-2xl p-6 text-center h-full">
                  <span className="font-display font-black text-4xl text-gold opacity-60 block mb-3">
                    {number}
                  </span>
                  <h3 className="font-display font-bold text-base text-hero-foreground mb-3">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            {...fadeUp(0.3)}
            className="text-hero-foreground font-body text-base leading-relaxed text-center max-w-2xl mx-auto opacity-80"
          >
            Our coaches guide you through every step — from your first
            single-leg attempt to chaining moves together under live pressure.
            You will build the kind of wrestling that makes your entire game
            harder to beat.
          </motion.p>
        </div>
      </section>

      {/* ── WHO IT'S FOR SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="wr-who"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div {...fadeUp(0.1)} className="text-center mb-10">
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Everyone Welcome
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-4">
              WHO IT'S FOR
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={title} {...fadeUp(0.15 + i * 0.1)}>
                <div className="bg-card rounded-2xl border border-border p-6 flex flex-col gap-4 shadow-card hover:shadow-elevated transition-smooth h-full">
                  <span className="w-11 h-11 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                    <Icon className="w-5 h-5 text-foreground" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-base text-foreground mb-2">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">
                      {desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section
        className="py-20 px-4 text-center relative"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.12 0.06 255) 0%, oklch(0.18 0.08 270) 50%, oklch(0.14 0.05 255) 100%)",
        }}
        data-ocid="wr-cta"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 50%, oklch(0.68 0.16 82) 0%, transparent 60%)",
          }}
          aria-hidden="true"
        />
        <motion.div
          {...fadeUp(0.1)}
          className="relative container mx-auto max-w-2xl"
        >
          <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
            First Class Is Free
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
            Start Your Wrestling Journey Today
          </h2>
          <p className="text-hero-foreground font-body text-lg leading-relaxed mb-3 max-w-lg mx-auto">
            Your first class is free. No commitment, no experience required —
            just show up and see what you can do.
          </p>
          <p className="text-hero-foreground font-body text-sm mb-8 opacity-70">
            Must book in advance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={() => router.navigate({ to: "/contact-us" })}
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
              data-ocid="wr-cta-button"
            >
              Book Your Free Class
            </button>
            <a
              href="tel:+15416003371"
              className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
              data-ocid="wr-cta-phone"
            >
              Call (541) 600-3371
            </a>
          </div>
        </motion.div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="wr-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div {...fadeUp(0.1)}>
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving Springfield &amp; Surrounding Areas
              </span>
            </div>
            <h2 className="font-display font-bold text-lg text-foreground mb-3">
              Wrestling Classes in Springfield, OR
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mb-4">
              Serving Springfield, Eugene, Thurston, Glenwood, Fairmont, and
              surrounding Lane County areas.
            </p>
            <address className="not-italic font-body text-sm text-muted-foreground mb-5">
              <span className="font-medium text-foreground">
                1485 Mohawk Blvd, Springfield, OR 97477
              </span>
              {" · "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>
              {" · "}
              <a
                href="mailto:shjjoffice@gmail.com"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                shjjoffice@gmail.com
              </a>
            </address>
            <div className="flex flex-wrap justify-center gap-2">
              {["Springfield", ...serviceAreas, "Lane County"].map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
